<script lang="ts" setup>
import { CheckInCircleBlackIcon } from '@metapic-apps/shared'
import { ElIcon } from 'element-plus'
import { computed, ref } from 'vue'

import CheckInCircleIcon from '@/assets/icons/check-in-circle.svg?component'
import CoinIcon from '@/assets/icons/coin.svg?component'
import CommentIcon from '@/assets/icons/comment.svg?component'
import SeedingSpendingsIcon from '@/assets/icons/seeding-spendings.svg?component'
import UserCardAvatarIcon from '@/assets/icons/user-default.svg?component'
import { formatMoney } from '@/lib/helpers/currency.helpers'
import type { CurrencyType } from '@/lib/types'
import type { CampaignParticipantType } from '@/types/campaignParticipant.types'
import BoxComponent from '@/ui-library/atoms/BoxComponent/BoxComponent.vue'
import FlexComponent from '@/ui-library/atoms/FlexComponent/FlexComponent.vue'
import ImageComponent from '@/ui-library/atoms/ImageComponent.vue'
import TypographyComponent from '@/ui-library/atoms/TypographyComponent/TypographyComponent.vue'

const { item, hasFixedFee } = defineProps<{ item: CampaignParticipantType; currency: CurrencyType; hasFixedFee: boolean }>()
const isAvatarErrorLoad = ref(false)

const connectedSocialAccounts = computed(() => item.socialMedia?.filter((social) => social.isConnected) ?? [])

const hasExtraContent = computed(() => {
  return (
    hasFixedFee ||
    item.todo?.length ||
    item.paymentAmountV2 ||
    item.offerCommentsCount ||
    item.productSeedingSpending ||
    connectedSocialAccounts.value.length
  )
})
const todoValue = computed(() => {
  if (!item.todo?.length) {
    return null
  }
  const finishedItems = item.todo?.filter((item) => item.value).length
  return `${finishedItems} / ${item.todo?.length}`
})
const errorHandler = () => (isAvatarErrorLoad.value = true)
</script>
<template>
  <BoxComponent
    :p="12"
    class="participant-card__container"
    :class="{ 'participant-card__container--has-updates': item.hasUnreadNotifications }"
  >
    <FlexComponent align="center" :gap="8">
      <ImageComponent
        v-if="item.creatorAvatar && !isAvatarErrorLoad"
        :image="item.creatorAvatar"
        :width="48"
        :alt="item.displayName"
        :height="48"
        class="participant-card__avatar"
        @on-load-error="errorHandler"
      />
      <ElIcon v-else :size="19" class="participant-card__default-avatar">
        <UserCardAvatarIcon />
      </ElIcon>
      <TypographyComponent variant="title-medium" :text-truncate="true">
        {{ item.displayName }}
      </TypographyComponent>
    </FlexComponent>
    <BoxComponent v-if="hasExtraContent" :mt="8">
      <FlexComponent :gap="8" wrap="wrap" align="center">
        <FlexComponent v-if="connectedSocialAccounts.length" align="center" :gap="4">
          <a
            v-for="socialAccount in connectedSocialAccounts"
            :key="socialAccount.id"
            :href="socialAccount.link"
            target="_blank"
            class="social-media-account social-media-account__icon-container"
          >
            <ElIcon :size="18" color="var(--primary)">
              <component :is="socialAccount.icon" />
            </ElIcon>
            <ElIcon :size="14" class="social-media-account__check-icon">
              <CheckInCircleBlackIcon />
            </ElIcon>
          </a>
        </FlexComponent>
        <FlexComponent v-if="todoValue" align="center" :gap="2">
          <ElIcon :size="18">
            <CheckInCircleIcon />
          </ElIcon>
          <TypographyComponent variant="label-small">{{ todoValue }}</TypographyComponent>
        </FlexComponent>
        <FlexComponent v-if="item.offerCommentsCount" align="center" :gap="2">
          <ElIcon :size="18">
            <CommentIcon />
          </ElIcon>
          <TypographyComponent variant="label-small">{{ item.offerCommentsCount }}</TypographyComponent>
        </FlexComponent>
        <FlexComponent v-if="item.paymentAmountV2" align="center" :gap="2">
          <ElIcon :size="18">
            <CoinIcon />
          </ElIcon>
          <TypographyComponent v-if="item.paymentAmountV2 && currency" variant="label-small">
            {{ formatMoney(item.paymentAmountV2.amount, currency) }}
          </TypographyComponent>
        </FlexComponent>
        <FlexComponent v-if="item.productSeedingSpending" align="center" :gap="2">
          <ElIcon :size="18">
            <SeedingSpendingsIcon />
          </ElIcon>
          <TypographyComponent variant="label-small">
            {{ formatMoney(item.productSeedingSpending, currency) }}
          </TypographyComponent>
        </FlexComponent>
      </FlexComponent>
    </BoxComponent>
  </BoxComponent>
</template>
<style lang="scss" scoped>
.participant-card__container {
  position: relative;
  margin-bottom: 8px;
  border-radius: 16px;
  background: #fff;
  cursor: pointer;
}

.participant-card__container--has-updates:before {
  content: '';
  position: absolute;
  right: 8px;
  top: 8px;
  width: 8px;
  height: 8px;
  background: #ea4f85;
  border-radius: 50%;
}

.participant-card__avatar {
  width: 48px;
  height: 48px;
  border-radius: 48px;
}
.participant-card__default-avatar {
  background-color: #fbe9f2;
  border-radius: 50%;
  flex-shrink: 0;
  width: 48px;
  height: 48px;
}

.social-media-account__icon-container {
  display: inline-flex;
  position: relative;
}
.social-media-account__check-icon {
  position: absolute;
  top: -4px;
  right: -4px;
  background-color: var(--surface);
  border-radius: 50%;
}

.social-media-account__icon-container :deep(.el-icon) {
  path {
    fill: currentColor;
  }
}
</style>
